/**
 * GitlabConnectModal — self-serve GitLab setup to PARITY with GitHub.
 *
 * Shared between:
 *   - IntegrationsPage (the canonical Settings → Integrations page)
 *   - MarketplaceDetailModal (Connect button on a marketplace card whose
 *     workflow declares `gitlab` as a required integration).
 *
 * Three sections (the modal grows as the user progresses), all so a tenant can
 * wire GitLab END-TO-END without anyone hand-writing DynamoDB rows / tokens:
 *
 *   1. CONNECT — instanceUrl + Personal Access Token, PLUS an optional
 *      `signingToken` (the GitLab-generated `whsec_…` per-webhook secret). The
 *      backend (handlers/gitlab.js connectGitlab) already accepts + stores it
 *      encrypted; the inbound webhook (gitlab-webhook.js) HMAC-validates each
 *      delivery against it. Optional — omit and the legacy static-token path
 *      still works.
 *
 *   2. WEBHOOK SETUP — surfaces the webhook URL + the secret the user must
 *      paste into GitLab (Project/Group → Settings → Webhooks), with the exact
 *      events to enable (Merge request + Comments). Mirrors how a GitHub App
 *      install surfaces its webhook config — here the user configures it by
 *      hand in their GitLab, so we SHOW the values + instructions.
 *
 *   3. REPO/GROUP → AGENT PICKER — the GitLab analog of the GitHub repo→agent
 *      link. The user picks a Zibby project, one of that project's deployed
 *      agents (workflows), and a GitLab project/group, then creates the link
 *      (POST /gitlab/links). That row is EXACTLY what gitlab-webhook.js
 *      resolveLink reads to route an inbound MR to the right review agent.
 *
 * The connect call + links CRUD live in hooks (useConnectGitlab /
 * useGitlabLinks / useCreateGitlabLink / useDeleteGitlabLink) so this component
 * stays presentation-focused. Reset transient state on isOpen transitions so
 * reopening always starts clean.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { IonModal, IonButton, IonSpinner } from '@ionic/react';
import {
  useConnectGitlab,
  useAllIntegrationStatuses,
  useGitlabLinks,
  useCreateGitlabLink,
  useDeleteGitlabLink,
} from '../../hooks/useIntegrations';
import api from '../../services/api';
// Reuses the existing .gitlab-*-modal CSS shipped with IntegrationsPage —
// importing here so the styles travel with the component wherever it's
// rendered, instead of relying on the host page having loaded the sheet.
import '../../pages/IntegrationsPage/IntegrationsPage.css';

// Derive the inbound webhook URL the user must configure in GitLab. The
// Webhooks RestApi has a prod custom domain (webhooks-api-prod.zibby.app);
// non-prod points at the API base. We key off REACT_APP_API_BASE_URL (the build
// stamps `https://api-prod.zibby.app` for prod) so the displayed URL matches
// the deployed webhook route.
function webhookUrl() {
  const apiBase = process.env.REACT_APP_API_BASE_URL || '';
  const isProd = /api-prod\.zibby\.app/.test(apiBase) || process.env.NODE_ENV === 'production';
  const stage = isProd ? 'prod' : 'dev';
  return `https://webhooks-api-${stage}.zibby.app/gitlab/webhook`;
}

export default function GitlabConnectModal({ isOpen, onClose, onConnected }) {
  // ── Connect form state ──────────────────────────────────────────────────
  const [instanceUrl, setInstanceUrl] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [signingToken, setSigningToken] = useState('');
  const [error, setError] = useState('');
  const [connecting, setConnecting] = useState(false);
  const connectMutation = useConnectGitlab();

  // Connection status drives whether we show the connect form or jump straight
  // to the webhook-setup + link-picker sections (so a re-open after connect
  // lands the user on the next step).
  const statusQuery = useAllIntegrationStatuses();
  const gitlabConnected = !!statusQuery.data?.gitlab?.connected;

  useEffect(() => {
    if (isOpen) {
      setInstanceUrl('');
      setAccessToken('');
      setSigningToken('');
      setError('');
      setConnecting(false);
    }
  }, [isOpen]);

  const handleConnect = () => {
    if (!instanceUrl.trim() || !accessToken.trim()) {
      setError('GitLab URL and Personal Access Token are required.');
      return;
    }
    setConnecting(true);
    setError('');
    connectMutation.mutate(
      {
        instanceUrl: instanceUrl.trim(),
        accessToken: accessToken.trim(),
        signingToken: signingToken.trim(), // optional; hook drops it if blank
      },
      {
        onSuccess: () => {
          setConnecting(false);
          onConnected?.();
          // Stay open — surface the webhook setup + link picker next. status
          // query re-fetches via the hook's cache invalidation.
        },
        onError: (err) => {
          setConnecting(false);
          setError(err?.message || 'Failed to connect GitLab. Please check your URL and token.');
        },
      },
    );
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => onClose?.()}
      className="gitlab-connect-modal"
    >
      {/* Stop click propagation at the modal-content level so a click inside
          doesn't bubble up to a parent (marketplace) modal backdrop. */}
      <div
        className="gitlab-modal-content"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2>Connect GitLab</h2>

        {/* ── Section 1: CONNECT ──────────────────────────────────────────── */}
        {!gitlabConnected && (
          <>
            <p className="gitlab-modal-description">
              Enter your self-hosted GitLab instance URL and a Personal Access Token with <code>read_api</code> and <code>read_repository</code> scopes.
            </p>

            <div className="gitlab-form-group">
              <label className="gitlab-form-label">GitLab Instance URL</label>
              <input
                type="text"
                value={instanceUrl}
                placeholder="https://gitlab.company.com"
                onChange={(e) => { setInstanceUrl(e.target.value || ''); setError(''); }}
                className="gitlab-form-input"
                disabled={connecting}
              />
            </div>

            <div className="gitlab-form-group">
              <label className="gitlab-form-label">Personal Access Token</label>
              <input
                type="password"
                value={accessToken}
                placeholder="glpat-xxxxxxxxxxxxxxxxxxxx"
                onChange={(e) => { setAccessToken(e.target.value || ''); setError(''); }}
                className="gitlab-form-input"
                disabled={connecting}
              />
            </div>

            <div className="gitlab-form-group">
              <label className="gitlab-form-label">
                Webhook Signing Token <span className="gitlab-form-optional">(optional)</span>
              </label>
              <input
                type="password"
                value={signingToken}
                placeholder="whsec_xxxxxxxxxxxxxxxxxxxx"
                onChange={(e) => { setSigningToken(e.target.value || ''); setError(''); }}
                className="gitlab-form-input"
                disabled={connecting}
              />
              <p className="gitlab-form-hint">
                Paste the signing token GitLab shows when you add the webhook below. It lets Zibby verify each delivery is genuinely from your GitLab. Leave blank to use the shared secret token instead.
              </p>
            </div>

            {error && <p className="gitlab-modal-error">{error}</p>}

            <div className="gitlab-modal-buttons">
              <IonButton
                expand="block"
                onClick={handleConnect}
                disabled={connecting || !instanceUrl.trim() || !accessToken.trim()}
              >
                {connecting ? <IonSpinner name="crescent" /> : 'Connect'}
              </IonButton>
              <IonButton
                expand="block"
                fill="clear"
                onClick={() => onClose?.()}
                disabled={connecting}
              >
                Cancel
              </IonButton>
            </div>
          </>
        )}

        {/* ── Section 2: WEBHOOK SETUP + Section 3: REPO→AGENT PICKER ─────── */}
        {gitlabConnected && (
          <>
            <WebhookSetup />
            <RepoAgentPicker />
            <div className="gitlab-modal-buttons">
              <IonButton expand="block" fill="clear" onClick={() => onClose?.()}>
                Done
              </IonButton>
            </div>
          </>
        )}
      </div>
    </IonModal>
  );
}

/**
 * Section 2 — show the webhook URL + secret the user must configure in GitLab,
 * plus the exact events to enable. Mirrors how the GitHub setup surfaces its
 * webhook config; here the user wires it by hand in their GitLab.
 */
function WebhookSetup() {
  const [copied, setCopied] = useState('');
  const url = webhookUrl();

  const copy = (text, which) => {
    try {
      navigator.clipboard?.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(''), 1500);
    } catch {
      /* clipboard unavailable — the value is still visible to copy manually */
    }
  };

  return (
    <div className="gitlab-setup-section">
      <h3 className="gitlab-section-title">1. Add a webhook in GitLab</h3>
      <p className="gitlab-modal-description">
        In your GitLab project (or group) go to <code>Settings → Webhooks → Add new webhook</code> and use these values:
      </p>

      <div className="gitlab-form-group">
        <label className="gitlab-form-label">Webhook URL</label>
        <div className="gitlab-copy-row">
          <input className="gitlab-form-input" value={url} readOnly onFocus={(e) => e.target.select()} />
          <button type="button" className="gitlab-copy-btn" onClick={() => copy(url, 'url')}>
            {copied === 'url' ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>

      <p className="gitlab-modal-description" style={{ marginBottom: 8 }}>
        <strong>Secret token:</strong> paste the Signing Token you entered when connecting (or set GitLab&apos;s
        secret token to your shared Zibby value). <br />
        <strong>Trigger:</strong> enable <code>Merge request events</code> and <code>Comments</code>.
      </p>
    </div>
  );
}

/**
 * Section 3 — repo/group → agent picker. The GitLab analog of the GitHub
 * repo→agent link. Creating a link POSTs to /gitlab/links, which writes the
 * exact row gitlab-webhook.js resolveLink reads to route an inbound MR.
 */
function RepoAgentPicker() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [workflows, setWorkflows] = useState([]);
  const [agentSlug, setAgentSlug] = useState('');
  const [glRepos, setGlRepos] = useState([]);
  const [kind, setKind] = useState('project'); // 'project' | 'group' | 'path'
  const [identifier, setIdentifier] = useState('');
  const [pickerError, setPickerError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const linksQuery = useGitlabLinks(projectId);
  const createLink = useCreateGitlabLink(projectId);
  const deleteLink = useDeleteGitlabLink(projectId);

  // Load the user's Zibby projects once.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.get('/projects');
        if (alive) setProjects(Array.isArray(data) ? data : (data?.projects || []));
      } catch {
        /* non-fatal — the picker just shows no projects */
      }
    })();
    return () => { alive = false; };
  }, []);

  // Load the GitLab projects (for the project/path identifier dropdown).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.get('/gitlab/repos');
        if (alive) setGlRepos(Array.isArray(data?.repos) ? data.repos : []);
      } catch {
        /* non-fatal */
      }
    })();
    return () => { alive = false; };
  }, []);

  // Load the selected project's deployed agents (workflows).
  useEffect(() => {
    let alive = true;
    setAgentSlug('');
    if (!projectId) { setWorkflows([]); return undefined; }
    (async () => {
      try {
        const data = await api.get(`/projects/${projectId}/workflows`);
        if (alive) setWorkflows(Array.isArray(data) ? data : []);
      } catch {
        if (alive) setWorkflows([]);
      }
    })();
    return () => { alive = false; };
  }, [projectId]);

  // Reset the identifier when switching kind (a project id is meaningless as a
  // group path and vice versa).
  useEffect(() => { setIdentifier(''); }, [kind]);

  // GitLab repos as { id, full_name } for the project/path dropdowns.
  const glOptions = useMemo(() => glRepos.map((r) => ({
    id: r.id,
    fullName: r.full_name,
  })), [glRepos]);

  const links = linksQuery.data?.links || [];

  const handleCreate = () => {
    setPickerError('');
    if (!projectId) { setPickerError('Pick a Zibby project.'); return; }
    if (!agentSlug) { setPickerError('Pick an agent to review merge requests.'); return; }
    if (!identifier) { setPickerError('Pick or enter the GitLab project / group.'); return; }
    setSubmitting(true);
    createLink.mutate(
      { kind, identifier: identifier.trim(), projectId, agentSlug },
      {
        onSuccess: () => { setSubmitting(false); setIdentifier(''); },
        onError: (err) => {
          setSubmitting(false);
          setPickerError(err?.message || 'Failed to create link.');
        },
      },
    );
  };

  const handleDelete = (link) => {
    deleteLink.mutate({ kind: link.glKind, identifier: link.glIdentifier });
  };

  return (
    <div className="gitlab-setup-section">
      <h3 className="gitlab-section-title">2. Link a GitLab project to a review agent</h3>
      <p className="gitlab-modal-description">
        Choose which deployed agent reviews merge requests for a GitLab project or group.
      </p>

      <div className="gitlab-form-group">
        <label className="gitlab-form-label">Zibby project</label>
        <select className="gitlab-form-input" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
          <option value="">Select a project…</option>
          {projects.map((p) => (
            <option key={p.projectId || p.id} value={p.projectId || p.id}>
              {p.name || p.projectName || p.projectId || p.id}
            </option>
          ))}
        </select>
      </div>

      <div className="gitlab-form-group">
        <label className="gitlab-form-label">Review agent</label>
        <select
          className="gitlab-form-input"
          value={agentSlug}
          onChange={(e) => setAgentSlug(e.target.value)}
          disabled={!projectId}
        >
          <option value="">{projectId ? 'Select an agent…' : 'Pick a project first'}</option>
          {workflows.map((w) => (
            <option key={w.workflowType} value={w.workflowType}>
              {w.displayName || w.workflowType}
            </option>
          ))}
        </select>
      </div>

      <div className="gitlab-form-group">
        <label className="gitlab-form-label">Link type</label>
        <select className="gitlab-form-input" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="project">A single GitLab project</option>
          <option value="group">A whole GitLab group (all projects under it)</option>
          <option value="path">A GitLab project by path</option>
        </select>
      </div>

      <div className="gitlab-form-group">
        <label className="gitlab-form-label">
          {kind === 'group' ? 'GitLab group path or id' : 'GitLab project'}
        </label>
        {kind === 'project' ? (
          <select className="gitlab-form-input" value={identifier} onChange={(e) => setIdentifier(e.target.value)}>
            <option value="">Select a GitLab project…</option>
            {glOptions.map((o) => (
              <option key={o.id} value={String(o.id)}>{o.fullName} (id {o.id})</option>
            ))}
          </select>
        ) : kind === 'path' ? (
          <select className="gitlab-form-input" value={identifier} onChange={(e) => setIdentifier(e.target.value)}>
            <option value="">Select a GitLab project…</option>
            {glOptions.map((o) => (
              <option key={o.id} value={o.fullName}>{o.fullName}</option>
            ))}
          </select>
        ) : (
          <input
            className="gitlab-form-input"
            value={identifier}
            placeholder="acme/team-a"
            onChange={(e) => setIdentifier(e.target.value || '')}
          />
        )}
      </div>

      {pickerError && <p className="gitlab-modal-error">{pickerError}</p>}

      <IonButton
        expand="block"
        onClick={handleCreate}
        disabled={submitting || !projectId || !agentSlug || !identifier}
      >
        {submitting ? <IonSpinner name="crescent" /> : 'Add link'}
      </IonButton>

      {/* Existing links for the selected project */}
      {projectId && links.length > 0 && (
        <div className="gitlab-links-list">
          {links.map((l) => (
            <div key={l.PK} className="gitlab-link-row">
              <span className="gitlab-link-label">
                <code>{l.glKind}</code> {l.glIdentifier} → <strong>{l.agentSlug}</strong>
              </span>
              <button type="button" className="gitlab-link-remove" onClick={() => handleDelete(l)}>
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
